import { GridRowId } from '@mui/x-data-grid';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import MaterialTable from '../../components/dataTable/MaterialTable/MaterialTable';
import TitleWrapper from '../../components/wrappers/titleWrapper/TitleWrapper';
import useDeleteGame from '../../hooks/api/games/useDeleteGame';
import useGameReport from '../../hooks/api/games/useGameReport';
import useHistoryGames from '../../hooks/api/games/useHistoryGames';
import { elementsIds } from '../../static/elementsIds';
import { QueryAliases } from '../../types/QueryAliases';
import HistoryGamesTableSelectionPanel from './components/HistoryGamesTableSelectionPanel';
import useHistoryGamesTableHeaders from './hooks/useHistoryGamesTableHeaders';

const PAGE_SIZE = 20;

const HistoryGamesView = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const { games, isLoading: isLoadingGames } = useHistoryGames({ page, limit: PAGE_SIZE });
  const { deleteGame, isLoading: isLoadingDelete } = useDeleteGame({
    onSuccess: () => {
      queryClient.refetchQueries([QueryAliases.HISTORY_GAMES]);
    },
  });
  const { downloadReport } = useGameReport();

  const [selectedGamesIds, setSelectedGamesIds] = useState<number[]>([]);

  const isSingleItemSelected = selectedGamesIds?.length === 1;

  const handleGetReportByGameId = async (id: number) => {
    downloadReport({ gameId: id });
  };

  const handleDeleteSelectedGame = async (ids: GridRowId[]) => {
    ids.forEach(async id => {
      deleteGame(id.toString());
    });
  };

  const gamesToShow = games?.data ?? [];

  const { headers } = useHistoryGamesTableHeaders({
    onGetReportClick: id => {
      downloadReport({ gameId: id });
    },
  });

  return (
    <TitleWrapper title={t('historyGames.title')}>
      <div id={elementsIds.historyGamesTable} className="flex mb-8 gap-3">
        <MaterialTable
          key={page}
          columns={headers}
          rows={gamesToShow}
          rowCount={games?.count ?? 0}
          checkboxSelection
          rowSelectionModel={selectedGamesIds}
          onRowSelectionModelChange={val => {
            setSelectedGamesIds(val as number[]);
          }}
          showPagination
          paginationMode="server"
          paginationModel={{ page: page - 1, pageSize: PAGE_SIZE }}
          onPaginationModelChange={e => {
            setPage(e.page + 1);
          }}
          pageSizeOptions={[PAGE_SIZE]}
          loading={isLoadingGames}
        />
      </div>

      {selectedGamesIds.length > 0 && (
        <HistoryGamesTableSelectionPanel
          tableId={elementsIds.historyGamesTable}
          selectedRowsNumber={selectedGamesIds.length}
          hideGetReportBtn={selectedGamesIds.length > 1}
          onGetReportClick={() => {
            if (isSingleItemSelected) {
              handleGetReportByGameId(selectedGamesIds[0]!);
            }
          }}
          onDelete={() => {
            handleDeleteSelectedGame(selectedGamesIds);
          }}
          isLoadingDelete={isLoadingDelete}
        />
      )}
    </TitleWrapper>
  );
};

export default HistoryGamesView;
